import { getToken, getUserRoles } from "../authorization/AuthorizationUtils";

const baseUrl = process.env.REACT_APP_SERVICE_API_URL;

interface Api {
  get: (url: string) => Promise<any>;
  upload: (url: string, imageData: any) => Promise<any>;
  post: (url: string, postData: any) => Promise<any>;
  put: (url: string, putData: any) => Promise<any>;
  delete: (url: string) => Promise<any>;
}

const promise = async (
  apiUrl: string,
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  postData?: any
) => {
  // Get both token and roles in parallel
  const [token, roles] = await Promise.all([getToken(), getUserRoles()]);
  
  // Prepare headers
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  
  // Add Authorization header if token exists
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  // Add X-User-Roles custom header if roles exist
  if (roles && roles.length > 0) {
    headers["X-User-Roles"] = JSON.stringify(roles); // TODO: Check if this is safe? 
  }
  
  return fetch(apiUrl, {
    body: JSON.stringify(postData),
    method,
    headers,
    mode: "cors",
  });
};

const uploadPromise = async (apiUrl: string, formData: any) => {
  // Get both token and roles in parallel
  const [token, roles] = await Promise.all([getToken(), getUserRoles()]);
  
  // Prepare headers
  const headers: Record<string, string> = {
    accept: "application/json",
  };
  
  // Add Authorization header if token exists
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  // Add X-User-Roles custom header if roles exist
  if (roles && roles.length > 0) {
    headers["X-User-Roles"] = JSON.stringify(roles);
  }
  
  return fetch(apiUrl, {
    method: "POST",
    headers,
    mode: "cors",
    body: formData,
  });
};

const handleResponse = (response: Response) => {
  if (response.status === 201 || response.status === 204)
    return Promise.resolve();
  if (response.status >= 200 && response.status < 300)
    return response.text().then((text) => (text ? JSON.parse(text) : {}));
  throw response;
};

const handleUploadResponse = (response: Response) => {
  if (response.status === 201 || response.status === 204)
    return response.json().then((data) => data);
  if (response.status >= 200 && response.status < 300)
    return response.text().then((text) => (text ? JSON.parse(text) : {}));
  throw response;
};

const customPromise = async (apiUrl: string, postData: any) => {
  // Get both token and roles in parallel
  const [token, roles] = await Promise.all([getToken(), getUserRoles()]);
  
  // Prepare headers
  const headers: Record<string, string> = {
    "Content-Type": "application/x-www-form-urlencoded",
  };
  
  // Add Authorization header if token exists
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  // Add X-User-Roles custom header if roles exist
  if (roles && roles.length > 0) {
    headers["X-User-Roles"] = JSON.stringify(roles);
  }
  
  return fetch(apiUrl, {
    method: "POST",
    headers,
    body: postData,
    mode: "cors",
  });
};

export const api: Api = {
  get: (url) => promise(`${baseUrl}${url}`).then(handleResponse),
  upload: (url, uploadData) =>
    uploadPromise(`${baseUrl}${url}`, uploadData).then(handleUploadResponse),
  post: (url, postData) =>
    customPromise(`${baseUrl}${url}`, postData).then(handleUploadResponse),
  put: (url, putData) =>
    promise(`${baseUrl}${url}`, "PUT", putData).then(handleResponse),
  delete: (url) => promise(`${baseUrl}${url}`, "DELETE").then(handleResponse),
};
